import { useTranslation } from 'react-i18next';
import { isBrazil, isFrance, isHungary, isUkraine } from 'utils/countryCode';
import { CropConstants, cropWithOutFao } from 'utils/constants/Crop';
import category from 'assets/icons/category.svg';
import grain from 'assets/icons/grain.svg';
import cicloIcon from 'assets/icons/Ciclo.svg';
import corDoGraoIcon from 'assets/icons/Cor_do_Grao.svg';
import exigenciaFertilidadeIcon from 'assets/icons/Exigencia_de_fertilidade.svg';
import finalidadeIcon from 'assets/icons/Finalidade.svg';
import glifosatoIcon from 'assets/icons/Glifosato.svg';
import glufosinatoIcon from 'assets/icons/Glufosinato.svg';
import {
  ProductNameText,
  Title as CardTitle,
  TitleInformationText,
  ValueInformationText,
  ValueText,
  CardContainer,
  CardTitleContainer,
  CardContentContainer,
  ProductNameContainer,
  ProductInformationContainer,
  BreakoutsInformationContainer,
  BreakoutsOptionContainer,
  DividerStyled,
  DividerContainer,
  FaoContainer,
  FaoText,
  GrainTypeContainer,
  CategoryContainer,
  StyledTitleContainer,
  MainContainer,
  ValueInformationIcon,
  ValueInformationDescription,
  ViewApplicableField,
  ViewApplicableText,
  ViewApplicableIcon,
  HybridProfileText,
  CropProductNameContainer,
  HybridCollapse,
  HybridPanel,
  CharacteristicsGridContainer,
  CharacteristicsRow,
  CharacteristicItem,
  CharacteristicsIcon,
  HighlightsList,
} from './SeedCard.styles';
import { Skeleton } from 'syngenta-digital-cropwise-react-ui-kit';

interface SquareSvgProps {
  readonly color: string;
}

function SquareSvg({ color }: SquareSvgProps) {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <g id="field">
        <path
          id="icon/maps/area_24px"
          fillRule="evenodd"
          clipRule="evenodd"
          d="M5 3H7V5L17 5V3H19V5L21 5V7H19V17H21V19H19V21H17V19L7 19V21H5V19H3V17H5V7H3V5L5 5V3ZM7 17V7L17 7V17L7 17Z"
          fill={color}
        />
      </g>
    </svg>
  );
}

interface CharacteristicsGridProps {
  readonly characteristics: Record<string, string>;
}

function CharacteristicsGrid({ characteristics }: CharacteristicsGridProps) {
  const CHARACTERISTIC_ICONS: Record<string, string> = {
    Ciclo: cicloIcon,
    'Cor do Grão': corDoGraoIcon,
    'Tipo de Grão': finalidadeIcon,
    Finalidade: finalidadeIcon,
    Glufosinato: glufosinatoIcon,
    Glifosato: glifosatoIcon,
    'Exigência de fertilidade': exigenciaFertilidadeIcon,
  };

  const entries = Object.entries(characteristics);
  const rows: [string, string][][] = [];
  for (let i = 0; i < entries.length; i += 2) {
    rows.push(entries.slice(i, i + 2) as [string, string][]);
  }
  return (
    <CharacteristicsGridContainer>
      {rows.map((row, rowIdx) => (
        <CharacteristicsRow key={rowIdx}>
          {row.map(([key, value]) => {
            const parenIdx = key.indexOf(' (');
            const keyMain = parenIdx !== -1 ? key.slice(0, parenIdx) : key;
            const keyParenthetical = parenIdx !== -1 ? key.slice(parenIdx) : null;
            return (
              <CharacteristicItem key={key}>
                <CharacteristicsIcon>
                  <img src={CHARACTERISTIC_ICONS[keyMain] ?? grain} alt="" data-testid="characteristic-icon" />
                </CharacteristicsIcon>
                <TitleInformationText>
                  {keyMain}{keyParenthetical && <><br />{keyParenthetical}</>}{': '}
                  <ValueInformationText>{value}</ValueInformationText>
                </TitleInformationText>
              </CharacteristicItem>
            );
          })}
        </CharacteristicsRow>
      ))}
    </CharacteristicsGridContainer>
  );
}

export interface IProps {
  productName: string;
  cardTitle?: string;
  commercialName?: string;
  grainType?: string;
  grainYieldType?: string | null;
  virusResistance?: string;
  description?: string[] | string;
  locationSummary?: string | number;
  locationsYieldData?: number | string;
  avgYieldSummary?: string | number;
  plusMinCheck?: string | number;
  isSelected?: boolean;
  fao?: string | number;
  handleOnClick?: (productName: string) => void;
  isMobile?: boolean;
  isLoadingBreakoutsProductSummaryAPI?: boolean;
  cropName?: string;
  marketSegment?: string;
  herbicideTechnology?: string;
  broomrapeTechnology?: string;
  technology?: string;
  countryName?: string;
  index?: number;
  color?: string;
  isDDG?: boolean;
  productCatalogLoading?: boolean;
  recommendedFieldCount?: number;
  size?: number;
  unit: string;
  headline?: string;
  highlights?: string[];
  characteristics?: Record<string, string>;
}

function europeFormatNumber(number: number | string) {
  const parsedNum = typeof number === 'string' ? parseFloat(number.replace(',', '.')) : number;
  return parsedNum;
}

const technologyAliasMap = {
  IMI: 'Clearfield',
  'IMI+': 'Clearfield® Plus',
  Sulfo: 'Sulfonylurea-tolerant',
  Conventional: 'Conventional',
  AIR: 'A.I.R.™',
  Express: 'Express®',
};

export const getTechnologyAlias = (tech: string | undefined) => {
  if (!tech) {
    return '';
  }
  return technologyAliasMap[tech as keyof typeof technologyAliasMap] || tech;
};

const SunflowerProductContainer = ({
  marketSegment,
  herbicideTechnology,
  broomrapeTechnology,
  countryName,
}: {
  marketSegment: string;
  herbicideTechnology: string;
  broomrapeTechnology: string;
  countryName?: string;
}) => {
  const { t } = useTranslation();
  const data = [
    { name: 'Oil Trait', value: marketSegment },
    // { name: 'Herbicide Tech', value: herbicideTechnology }, // Temporarily comment out as per the request from PO
    ...(isFrance(countryName) ? [] : [{ name: 'Broomrape Trait', value: broomrapeTechnology }]),
  ];
  return (
    <MainContainer>
      {data.map((item) => (
        <StyledTitleContainer key={item.name}>
          <TitleInformationText>
            {t(item.name)}: <ValueInformationText>{t(item.value)}</ValueInformationText>
          </TitleInformationText>
        </StyledTitleContainer>
      ))}
    </MainContainer>
  );
};

const SeedCard = ({
  productName,
  cardTitle,
  commercialName,
  grainType,
  grainYieldType,
  virusResistance,
  description,
  locationSummary,
  locationsYieldData,
  avgYieldSummary,
  plusMinCheck,
  isSelected,
  fao,
  marketSegment = '',
  herbicideTechnology = '',
  broomrapeTechnology = '',
  isMobile = false,
  cropName,
  technology,
  handleOnClick,
  countryName,
  index,
  color,
  isDDG,
  recommendedFieldCount,
  size,
  unit,
  productCatalogLoading,
  isLoadingBreakoutsProductSummaryAPI,
  headline,
  highlights,
  characteristics,
}: IProps) => {
  const { t } = useTranslation();

  const renderDescription = () => {
    if (countryName === CropConstants.SOUTH_AFRICA && Array.isArray(description)) {
      return (
        <ul>
          {description?.map((item: string, index: number) => {
            return <li key={index}>{item}</li>;
          })}
        </ul>
      );
    }
    return description;
  };
  const renderProductInformation = () => (
    <ProductInformationContainer>
      {productCatalogLoading ? (
        <Skeleton active paragraph={{ rows: 1, width: '100%' }} />
      ) : (
        <>
          {isBrazil(countryName) && (headline || (highlights && highlights.length > 0) || (characteristics && Object.keys(characteristics).length > 0)) ? (
            <div>
              {headline && (
                <ValueInformationDescription>
                  <strong>{headline}</strong>
                </ValueInformationDescription>
              )}
              {highlights && highlights.length > 0 && (
                <HighlightsList>
                  {highlights.map((item, i) => (
                    <li key={i}>
                      <ValueInformationDescription>{item}</ValueInformationDescription>
                    </li>
                  ))}
                </HighlightsList>
              )}
              {isMobile && highlights && highlights.length > 0 && characteristics && Object.keys(characteristics).length > 0 && (
                <DividerContainer>
                  <DividerStyled />
                </DividerContainer>
              )}
              {characteristics && Object.keys(characteristics).length > 0 && (
                <CharacteristicsGrid characteristics={characteristics} />
              )}
            </div>
          ) : (
            <div>
              <ValueInformationDescription>{renderDescription()}</ValueInformationDescription>
            </div>
          )}
          {technology && (
            <CategoryContainer>
              <TitleInformationText>
                <ValueInformationIcon>
                  <img src={category} alt="category" />
                </ValueInformationIcon>
                {`${t('Category')}: `}
                <ValueInformationText>{t(getTechnologyAlias(technology))}</ValueInformationText>
              </TitleInformationText>
            </CategoryContainer>
          )}
          {virusResistance && (
            <CategoryContainer>
              <TitleInformationText>
                <ValueInformationIcon>
                  <img src={grain} alt="grain" />
                </ValueInformationIcon>
                {`${t('Virus Resistance')}: `}{' '}
                <ValueInformationText>{t(`${virusResistance}`)}</ValueInformationText>
              </TitleInformationText>
            </CategoryContainer>
          )}
          {grainType && (
            <GrainTypeContainer>
              <TitleInformationText>
                <ValueInformationIcon>
                  <img src={grain} alt="grain" />
                </ValueInformationIcon>
                {`${t('Grain Type')}: `}{' '}
                <ValueInformationText>{t(`${grainType}`)}</ValueInformationText>
              </TitleInformationText>
            </GrainTypeContainer>
          )}
          {grainYieldType && (
            <GrainTypeContainer>
              <TitleInformationText>
                <ValueInformationIcon>
                  <img src={grain} alt="grain" />
                </ValueInformationIcon>
                {`${t('Grain Yield Type')}: `}{' '}
                <ValueInformationText>{t(`${grainYieldType}`)}</ValueInformationText>
              </TitleInformationText>
            </GrainTypeContainer>
          )}
        </>
      )}
    </ProductInformationContainer>
  );

  const renderBreakoutsInformation = () => (
    <BreakoutsInformationContainer>
      <BreakoutsOptionContainer>
        <ValueInformationText>{t('Total Trials')}</ValueInformationText>

        {isLoadingBreakoutsProductSummaryAPI ? (
          <Skeleton.Input active> </Skeleton.Input>
        ) : (
          <ValueText>{locationSummary}</ValueText>
        )}
      </BreakoutsOptionContainer>
      <BreakoutsOptionContainer applyMargin>
        <ValueInformationText>{t('Matching Environments')}</ValueInformationText>
        {isLoadingBreakoutsProductSummaryAPI ? (
          <Skeleton.Input active> </Skeleton.Input>
        ) : (
          <ValueText>{locationsYieldData}</ValueText>
        )}
      </BreakoutsOptionContainer>
      <BreakoutsOptionContainer applyMargin>
        <ValueInformationText>
          {cropName === CropConstants.SUNFLOWER_SMALLCASE && isHungary(countryName)
            ? t('Average Yield Hungary')
            : t('Average Yield')}
        </ValueInformationText>
        {isLoadingBreakoutsProductSummaryAPI ? (
          <Skeleton.Input active> </Skeleton.Input>
        ) : (
          <ValueText>
            {avgYieldSummary && `${europeFormatNumber(avgYieldSummary)} ${t(unit)}`}
          </ValueText>
        )}
      </BreakoutsOptionContainer>
      {(isDDG || !isUkraine(countryName)) && (
        <BreakoutsOptionContainer>
          <ValueInformationText>+/- {t('Check')}%*</ValueInformationText>

          {isLoadingBreakoutsProductSummaryAPI ? (
            <Skeleton.Input active> </Skeleton.Input>
          ) : (
            <ValueText>{plusMinCheck && europeFormatNumber(plusMinCheck)}%</ValueText>
          )}
        </BreakoutsOptionContainer>
      )}
      {!isDDG && !isMobile && recommendedFieldCount !== 0 && (
        <ViewApplicableField color={color} onClick={() => handleOnClick?.(productName)}>
          <ViewApplicableIcon>{color && <SquareSvg color={color} />}</ViewApplicableIcon>
          <ViewApplicableText color={color}>
            {t('View applicable Fields', { count: recommendedFieldCount })}{' '}
          </ViewApplicableText>{' '}
        </ViewApplicableField>
      )}
    </BreakoutsInformationContainer>
  );

  return (
    <CardContainer isMobile={isMobile} selected={isSelected} isDDG={isDDG} size={size}>
      {index === 0 && (
        <CardTitleContainer selected={isSelected}>
          <CardTitle>{cardTitle}</CardTitle>
        </CardTitleContainer>
      )}
      <CardContentContainer>
        {cropName === CropConstants.SUNFLOWER_SMALLCASE ? (
          <ProductNameContainer isDDG={isDDG} selected={isSelected} isMobile={isMobile}>
            {productCatalogLoading ? (
              <Skeleton.Input active />
            ) : (
              <>
                {' '}
                <ProductNameText>{commercialName}</ProductNameText>
                <SunflowerProductContainer
                  marketSegment={marketSegment}
                  herbicideTechnology={herbicideTechnology}
                  broomrapeTechnology={broomrapeTechnology}
                  countryName={countryName}
                />
              </>
            )}
          </ProductNameContainer>
        ) : (
          <CropProductNameContainer isDDG={isDDG} selected={isSelected} isMobile={isMobile}>
            {productCatalogLoading ? (
              <Skeleton.Input active />
            ) : (
              <>
                <ProductNameText>{commercialName}</ProductNameText>
                {!cropWithOutFao.includes(cropName?.toLowerCase() || '') &&
                  !isBrazil(countryName) &&
                  cropName?.toLowerCase() !== CropConstants.SUNFLOWER_SMALLCASE && (
                  <FaoContainer isMobile={isMobile}>
                    <FaoText>
                      {'FAO '} {fao}
                    </FaoText>
                  </FaoContainer>
                )}
              </>
            )}
          </CropProductNameContainer>
        )}
        {isMobile ? (
          <HybridCollapse bordered={false} defaultActiveKey={index === 0 ? ['1'] : []} ghost={true}>
            <HybridPanel
              header={
                <HybridProfileText>
                  {t(
                    `${
                      cropName?.toLowerCase() === CropConstants.WHEAT_SMALLCASE
                        ? 'Variety'
                        : 'Hybrid'
                    } Profile`
                  )}
                </HybridProfileText>
              }
              key="1"
            >
              {renderProductInformation()}

              <DividerContainer>
                <DividerStyled />
              </DividerContainer>

              {renderBreakoutsInformation()}
            </HybridPanel>

            {!isDDG && recommendedFieldCount !== 0 && (
              <ViewApplicableField
                data-testid={'view-applicable-fields'}
                color={color}
                onClick={() => handleOnClick?.(productName)}
              >
                <ViewApplicableIcon>{color && <SquareSvg color={color} />}</ViewApplicableIcon>
                <ViewApplicableText color={color}>
                  {t('View applicable Fields', { count: recommendedFieldCount })}{' '}
                </ViewApplicableText>
              </ViewApplicableField>
            )}
          </HybridCollapse>
        ) : (
          <>
            {renderProductInformation()}
            <DividerContainer>
              <DividerStyled />
            </DividerContainer>
            {renderBreakoutsInformation()}
          </>
        )}
      </CardContentContainer>
    </CardContainer>
  );
};

export default SeedCard;
